// Centralized SEO utilities: site constants, head tag management, and JSON-LD injection.

export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string) || "https://cssbuybr.it.com";

export const SITE_NAME = "CSSBuy Spreadsheet";

export const FAVICON_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png";

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType: string;
}

export interface JsonLd {
  id: string;
  data: Record<string, unknown>;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  const selector = attr === "name" ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeo(data: SeoData) {
  const canonicalUrl = `${SITE_URL}${data.canonicalPath}`;

  document.title = data.title;

  upsertMeta("name", "description", data.description);
  upsertMeta("name", "keywords", data.keywords);
  upsertCanonical(canonicalUrl);

  upsertMeta("property", "og:title", data.title);
  upsertMeta("property", "og:description", data.description);
  upsertMeta("property", "og:type", data.ogType);
  upsertMeta("property", "og:url", canonicalUrl);
  upsertMeta("property", "og:site_name", SITE_NAME);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", data.title);
  upsertMeta("name", "twitter:description", data.description);
}

export function setJsonLd(id: string, data: Record<string, unknown> | null) {
  const scriptId = `seo-jsonld-${id}`;
  const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (data === null) {
    if (existing) {
      existing.remove();
    }
    return;
  }

  const script = existing || document.createElement("script");
  script.id = scriptId;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  if (!existing) {
    document.head.appendChild(script);
  }
}